import { inject, Injectable } from '@angular/core';
import { WorkflowService } from '@common/services/mediahaven/workflow.service';
import { SearchService, TaskAction, TaskId, UserStoreService, WaitForRecordStatusUpdate } from '@mediahaven/shared';
import {
    ApiFragment,
    CorrectMediaPayload,
    makeSequentialRequests,
    RecordStatus,
    TaskKey,
    User
} from '@stylehaven/components';
import { Observable } from 'rxjs';
import { mapTo, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SubmitActionSandbox {
    private readonly searchService: SearchService = inject(SearchService);
    constructor(
        private readonly userStoreService: UserStoreService,
        private readonly workflowService: WorkflowService
    ) {}

    // #region Submit Records
    public submitRecordForEvaluation(record: ApiFragment): Observable<boolean> {
        return this.userStoreService.getCurrentUser().pipe(
            switchMap(user => this.executeSubmitAction(record, user)),
            take(1) // MED-3755 The stream doesn't complete in this point due to the switchmap, take(1) resolves this
        );
    }

    private executeSubmitAction(record: ApiFragment, user: User): Observable<boolean> {
        const submitTaskAction: TaskAction = {
            ActionType: 'COMPLETE',
            CompletionVariables: {
                RevisionDescision: CorrectMediaPayload.Submit
            }
        };

        const taskId: TaskId | undefined = record
            .getTasks()
            .find(task => task.TypeId === TaskKey.CorrectMedia && task.AssigneeUserId === user.Id)?.Id as TaskId;

        const submitStream$: Observable<void | ApiFragment> =
            typeof taskId !== 'undefined' && record.getRecordStatus() === RecordStatus.RejectedForCorrection
                ? this.workflowService.submitTaskAction(taskId, submitTaskAction)
                : this.workflowService.submitRecordForEvaluation(record);
        return submitStream$.pipe(
            switchMap(() =>
                WaitForRecordStatusUpdate(record.getFragmentId(), this.searchService, record.getRecordStatus())
            ),
            mapTo(true)
        );
    }

    public bulkSubmitRecordsForEvaluation(records: readonly ApiFragment[]): Observable<boolean[]> {
        const requests$: Observable<boolean>[] = records.map(record => this.submitRecordForEvaluation(record));
        return makeSequentialRequests(requests$);
    }
    // #endregions
}
